/**
 * Driver-based planning (the way Jirav works): a client's plan is a set of drivers
 * (revenue streams, a headcount plan, expenses, capital purchases, loans, working-capital
 * days, distributions and tax) that produce a linked monthly P&L, balance sheet and cash
 * flow for 12 to 24 months.
 *
 * Rules:
 * - every amount is computed exactly and rounded to the cent once, per line per month;
 *   totals are sums of the lines shown;
 * - the three statements are linked: cash is opening cash plus the month's cash flow,
 *   and the balance sheet balances every month (assets = liabilities + equity), by
 *   construction and checked;
 * - percentages are exact decimal text ("2.5" = 2.5%), never floating point.
 */
import type { Cents } from "../money";
import { ssWageBaseEstimated, tableFor } from "../tax/tables";
import { Exact } from "./exact";
import { monthsUntilCashRunsOut, type CashOut } from "./forecast";

export type PlanMonth = string;

export type PlanRevenueStream =
  | { id: string; name: string; method: "growth"; base: Cents; growthPct: string; seasonality?: string[] | null; cogsPct: string }
  | { id: string; name: string; method: "units"; units: string; unitGrowthPct: string; price: Cents; cogsPct: string }
  | { id: string; name: string; method: "contract"; amount: Cents; start: PlanMonth; end: PlanMonth | null; cogsPct: string };

export type PlanDepartment = "cogs" | "operations" | "sales" | "admin";
export const DEPARTMENTS: Record<PlanDepartment, string> = { cogs: "Direct labor (cost of sales)", operations: "Operations", sales: "Sales & marketing", admin: "General & administrative" };

export type PlanRole = { id: string; title: string; department: PlanDepartment; count: number; annualSalary: Cents; start: PlanMonth; end: PlanMonth | null };

export type PlanExpense =
  | { id: string; name: string; method: "fixed"; amount: Cents; growthPct: string; start?: PlanMonth | null }
  | { id: string; name: string; method: "per_employee"; amount: Cents }
  | { id: string; name: string; method: "pct_revenue"; pct: string };

export type PlanPurchase = { id: string; name: string; month: PlanMonth; amount: Cents; lifeMonths: number };
export type PlanLoan = { id: string; name: string; month: PlanMonth; principal: Cents; annualRatePct: string; termMonths: number };

export type PlanDrivers = {
  revenue: PlanRevenueStream[];
  roles: PlanRole[];
  /**
   * Employer payroll taxes. Null (the default): worked out per person and calendar year from the
   * year's tax tables (see employerPayrollTaxes). A percent of salaries ("8.00") overrides that,
   * for a client whose pay runs show a different rate (state unemployment, for example).
   * Plans saved before October 2026 hold "7.65", the old flat default: it is read as null.
   */
  payrollTaxPct: string | null;
  /** Benefits per employee per month. */
  benefitsPerHead: Cents;
  expenses: PlanExpense[];
  purchases: PlanPurchase[];
  loans: PlanLoan[];
  /** Days sales outstanding, days payables outstanding, days inventory outstanding. */
  dsoDays: string;
  dpoDays: string;
  dioDays: string;
  /** Owner distributions per month. */
  distributions: Cents;
  /** Income tax on positive pre-tax income (0 for pass-through entities). */
  taxRatePct: string;
  /** Depreciation of assets already owned, per month (stops when they are fully depreciated). */
  existingDepreciation: Cents;
  /** Principal repaid each month on debt already owed, and its interest rate. */
  existingDebtPayment: Cents;
  existingDebtRatePct: string;
};

/** Balances at the end of the month before the plan starts (from QuickBooks). */
export type PlanOpening = {
  asOf: PlanMonth;
  cash: Cents;
  ar: Cents;
  inventory: Cents;
  /** Every other asset (prepaids, deposits, undeposited funds...), held constant. */
  otherAssets: Cents;
  fixedAssetsNet: Cents;
  ap: Cents;
  /** Every other liability that isn't debt (card balances, payroll liabilities...), held constant. */
  otherLiabilities: Cents;
  debt: Cents;
  equity: Cents;
};

export type PlanLine = { key: string; label: string; section: "revenue" | "cogs" | "opex"; values: Cents[] };

export type PlanOutput = {
  months: PlanMonth[];
  lines: PlanLine[];
  pnl: {
    revenue: Cents[];
    cogs: Cents[];
    grossProfit: Cents[];
    opex: Cents[];
    ebitda: Cents[];
    depreciation: Cents[];
    interest: Cents[];
    pretax: Cents[];
    tax: Cents[];
    netIncome: Cents[];
  };
  headcount: number[];
  /** Employer payroll taxes by month (inside the "Payroll taxes and benefits" line, without benefits). */
  payrollTaxes: Cents[];
  /**
   * Calendar years whose payroll taxes rest on an estimated Social Security wage base (SSA hasn't
   * announced it yet) because someone's pay that year reaches the base. Screens mark these years.
   * Empty when the plan uses a payroll tax percent instead.
   */
  payrollTaxEstimatedYears: number[];
  bs: { cash: Cents[]; ar: Cents[]; inventory: Cents[]; otherAssets: Cents[]; fixedAssetsNet: Cents[]; totalAssets: Cents[]; ap: Cents[]; otherLiabilities: Cents[]; debt: Cents[]; equity: Cents[]; totalLiabilitiesAndEquity: Cents[] };
  cf: { netIncome: Cents[]; depreciation: Cents[]; workingCapital: Cents[]; operating: Cents[]; investing: Cents[]; borrowing: Cents[]; repayments: Cents[]; distributions: Cents[]; financing: Cents[]; netChange: Cents[]; beginning: Cents[]; ending: Cents[] };
  summary: { revenue: Cents; netIncome: Cents; endingCash: Cents; lowestCash: Cents; lowestCashMonth: PlanMonth; firstNegativeMonth: PlanMonth | null; cashOut: CashOut; endingHeadcount: number; endingDebt: Cents };
};

export const EMPTY_DRIVERS: PlanDrivers = {
  revenue: [],
  roles: [],
  payrollTaxPct: null,
  benefitsPerHead: 0,
  expenses: [],
  purchases: [],
  loans: [],
  dsoDays: "30",
  dpoDays: "30",
  dioDays: "0",
  distributions: 0,
  taxRatePct: "0",
  existingDepreciation: 0,
  existingDebtPayment: 0,
  existingDebtRatePct: "0",
};

// --- helpers ---------------------------------------------------------------------------------

const ONE = Exact.int(1);
const pct = (s: string) => Exact.decimal(s).div(Exact.int(100));
const cents = (x: Exact) => x.toCents();

export function planMonths(start: PlanMonth, n: number): PlanMonth[] {
  const [y, m] = start.split("-").map(Number) as [number, number];
  return Array.from({ length: n }, (_, k) => {
    const idx = y * 12 + (m - 1) + k;
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
  });
}

const daysIn = (month: PlanMonth) => {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

const active = (m: PlanMonth, start: PlanMonth, end: PlanMonth | null) => m >= start && (end === null || m <= end);

/** Employer Social Security and Medicare rates, fixed in law (IRC 3111(a), (b)); the wage bases come from the year tables. */
const SS_EMPLOYER = Exact.decimal("0.062");
const MEDICARE_EMPLOYER = Exact.decimal("0.0145");
/** The flat default plans carried before October 2026; never chosen by anyone, so it means "work it out". */
const OLD_DEFAULT = Exact.decimal("7.65");

/** The plan's payroll tax percent when staff set one; null when taxes are worked out from the tax tables. */
export function payrollTaxOverride(d: Pick<PlanDrivers, "payrollTaxPct">): string | null {
  const v = (d.payrollTaxPct ?? "").trim();
  if (v === "") return null;
  try {
    return Exact.decimal(v).eq(OLD_DEFAULT) ? null : v;
  } catch {
    return v; // not a number: validateDrivers says so
  }
}

const minE = (a: Exact, b: Exact) => (a.cmp(b) <= 0 ? a : b);
const maxE = (a: Exact, b: Exact) => (a.cmp(b) >= 0 ? a : b);
const ZERO = Exact.int(0);

type YearRates = { ssBase: Exact; futaBase: Exact; futaRate: Exact };
const ratesCache = new Map<number, YearRates>();
function ratesFor(year: number): YearRates {
  let r = ratesCache.get(year);
  if (!r) {
    const t = tableFor(year);
    r = { ssBase: Exact.int(t.ssWageBase * 100), futaBase: Exact.int(t.futaWageBase * 100), futaRate: Exact.decimal(String(t.futaNetRate)) };
    ratesCache.set(year, r);
  }
  return r;
}

/**
 * Employer payroll taxes by month, worked out per person and calendar year: Social Security 6.2% on
 * the year's pay up to that year's wage base, Medicare 1.45% on all pay, and federal unemployment
 * (FUTA, net 0.6%) on the first $7,000 of each person's year. A person is paid a twelfth of the
 * salary each month; a role already working before the plan starts is taken to have been paid since
 * January (or since it started that year), so the wage bases are reached in the right month.
 * Exact throughout; each month is rounded to the cent once.
 */
/** Months this role was paid earlier in the calendar year of `m`. */
function monthsPaidBefore(m: PlanMonth, role: PlanRole): number {
  const year = m.slice(0, 4);
  let before = 0;
  for (let k = 1; k < Number(m.slice(5, 7)); k++) if (active(`${year}-${String(k).padStart(2, "0")}`, role.start, role.end)) before++;
  return before;
}

export function employerPayrollTaxes(roles: PlanRole[], months: PlanMonth[]): Cents[] {
  return months.map((m) => {
    const year = Number(m.slice(0, 4));
    const r = ratesFor(year);
    let total = ZERO;
    for (const role of roles) {
      if (role.count <= 0 || !active(m, role.start, role.end)) continue;
      const monthly = Exact.int(role.annualSalary).div(Exact.int(12));
      const ytd = monthly.mul(Exact.int(monthsPaidBefore(m, role)));
      const ss = minE(monthly, maxE(ZERO, r.ssBase.sub(ytd))).mul(SS_EMPLOYER);
      const medicare = monthly.mul(MEDICARE_EMPLOYER);
      const futa = minE(monthly, maxE(ZERO, r.futaBase.sub(ytd))).mul(r.futaRate);
      total = total.add(ss.add(medicare).add(futa).mul(Exact.int(role.count)));
    }
    return cents(total);
  });
}

/**
 * The calendar years among `months` whose employer payroll taxes depend on an estimated Social
 * Security wage base: the base isn't published or announced for that year (ssWageBaseEstimated) and
 * some role's pay that year, counted as employerPayrollTaxes counts it, goes past the base in a plan
 * month. Years where everyone stays under the base aren't listed: the base doesn't change their taxes.
 */
export function estimatedWageBaseYears(roles: PlanRole[], months: PlanMonth[]): number[] {
  const years = new Set<number>();
  for (const m of months) {
    const year = Number(m.slice(0, 4));
    if (years.has(year) || !ssWageBaseEstimated(year)) continue;
    const r = ratesFor(year);
    for (const role of roles) {
      if (role.count <= 0 || !active(m, role.start, role.end)) continue;
      const monthly = Exact.int(role.annualSalary).div(Exact.int(12));
      const through = monthly.mul(Exact.int(monthsPaidBefore(m, role) + 1));
      if (through.cmp(r.ssBase) > 0) {
        years.add(year);
        break;
      }
    }
  }
  return [...years].sort((a, b) => a - b);
}

/**
 * The yearly salary whose pay plus employer payroll taxes for a full calendar year comes to
 * `annualCost` (what the books show as payroll cost for one person), with that year's tables.
 */
export function salaryFromEmployerCost(annualCost: Cents, year: number): Cents {
  if (annualCost <= 0) return 0;
  const r = ratesFor(year);
  const cost = Exact.int(annualCost);
  const one = Exact.int(1);
  // Pay at most the FUTA base: every rate applies to all of it.
  const low = cost.div(one.add(SS_EMPLOYER).add(MEDICARE_EMPLOYER).add(r.futaRate));
  if (low.cmp(r.futaBase) <= 0) return low.toCents();
  // Between the FUTA base and the Social Security wage base.
  const mid = cost.sub(r.futaBase.mul(r.futaRate)).div(one.add(SS_EMPLOYER).add(MEDICARE_EMPLOYER));
  if (mid.cmp(r.ssBase) <= 0) return mid.toCents();
  // Above the Social Security wage base: only Medicare on the rest.
  return cost.sub(r.futaBase.mul(r.futaRate)).sub(r.ssBase.mul(SS_EMPLOYER)).div(one.add(MEDICARE_EMPLOYER)).toCents();
}

/** Level monthly payment that repays `principal` over `n` months at monthly rate r (r = 0: straight line), rounded up to the cent. */
export function loanPayment(principal: Cents, annualRatePct: string, n: number): Cents {
  if (!Number.isInteger(n) || n <= 0) throw new RangeError("A loan needs a term of at least one month");
  const r = pct(annualRatePct).div(Exact.int(12));
  if (r.isZero()) return Math.ceil(principal / n);
  let f = ONE;
  for (let i = 0; i < n; i++) f = f.mul(ONE.add(r));
  // P × r × f ÷ (f − 1)
  const exact = Exact.int(principal).mul(r).mul(f).div(f.sub(ONE));
  const c = cents(exact);
  return exact.cmp(Exact.int(c)) > 0 ? c + 1 : c;
}

/** Checks a plan's drivers for values that can't be modelled. Returns readable problems (empty when fine). */
export function validateDrivers(d: PlanDrivers, n: number, start?: PlanMonth): string[] {
  const out: string[] = [];
  const dec = (v: string, what: string, min = -100, max = 1000) => {
    let x: Exact;
    try {
      x = Exact.decimal(v);
    } catch {
      out.push(`${what}: enter a number`);
      return;
    }
    if (x.cmp(Exact.int(min)) < 0 || x.cmp(Exact.int(max)) > 0) out.push(`${what}: must be between ${min} and ${max}`);
  };
  if (![12, 18, 24].includes(n)) out.push("A plan covers 12, 18 or 24 months");
  for (const s of d.revenue) {
    dec(s.cogsPct, `${s.name}: cost of sales %`, 0, 100);
    if (s.method === "growth") {
      dec(s.growthPct, `${s.name}: monthly growth`, -50, 100);
      if (s.seasonality && (s.seasonality.length !== 12 || s.seasonality.some((x) => !/^\d{1,3}(\.\d{1,4})?$/.test(x)))) out.push(`${s.name}: seasonality needs twelve factors such as 1.00`);
    }
    if (s.method === "units") {
      dec(s.units, `${s.name}: starting units`, 0, 10_000_000);
      dec(s.unitGrowthPct, `${s.name}: monthly unit growth`, -50, 100);
    }
    if (s.method === "contract" && s.end !== null && s.end < s.start) out.push(`${s.name}: the contract ends before it starts`);
  }
  for (const r of d.roles) {
    if (!Number.isInteger(r.count) || r.count < 0 || r.count > 10_000) out.push(`${r.title}: number of people must be a whole number`);
    if (r.end !== null && r.end < r.start) out.push(`${r.title}: ends before it starts`);
  }
  for (const e of d.expenses) {
    if (e.method === "fixed") dec(e.growthPct, `${e.name}: monthly growth`, -50, 100);
    if (e.method === "pct_revenue") dec(e.pct, `${e.name}: percent of revenue`, 0, 100);
  }
  for (const p of d.purchases) if (!Number.isInteger(p.lifeMonths) || p.lifeMonths < 1 || p.lifeMonths > 600) out.push(`${p.name}: useful life must be 1 to 600 months`);
  for (const l of d.loans) {
    // Debt owed before the plan starts is the opening balance (existing debt), not a new loan.
    if (start && l.month < start) out.push(`${l.name}: borrowed before the plan starts; include it in existing debt instead`);
    if (!Number.isInteger(l.termMonths) || l.termMonths < 1 || l.termMonths > 480) out.push(`${l.name}: term must be 1 to 480 months`);
    dec(l.annualRatePct, `${l.name}: interest rate`, 0, 50);
  }
  const override = payrollTaxOverride(d);
  if (override !== null) dec(override, "Payroll taxes %", 0, 50);
  dec(d.dsoDays, "Days sales outstanding", 0, 365);
  dec(d.dpoDays, "Days payables outstanding", 0, 365);
  dec(d.dioDays, "Days inventory outstanding", 0, 730);
  dec(d.taxRatePct, "Income tax rate", 0, 60);
  dec(d.existingDebtRatePct, "Existing debt interest rate", 0, 50);
  return out;
}

/** Runs the plan. Throws RangeError for drivers that fail validateDrivers. */
export function runPlan(d: PlanDrivers, opening: PlanOpening, start: PlanMonth, n: number): PlanOutput {
  const problems = validateDrivers(d, n, start);
  if (problems.length) throw new RangeError(problems.join("; "));
  const months = planMonths(start, n);
  const zeros = () => months.map(() => 0);
  const lines: PlanLine[] = [];
  const line = (key: string, label: string, section: PlanLine["section"]) => {
    const l: PlanLine = { key, label, section, values: zeros() };
    lines.push(l);
    return l;
  };

  // Revenue and its direct cost, stream by stream.
  const revLines = d.revenue.map((s) => ({ s, rev: line(`rev:${s.id}`, s.name, "revenue"), cogs: line(`cogs:${s.id}`, `${s.name} — cost of sales`, "cogs") }));
  for (const { s, rev, cogs } of revLines) {
    let factor = ONE;
    const growth = s.method === "growth" ? ONE.add(pct(s.growthPct)) : s.method === "units" ? ONE.add(pct(s.unitGrowthPct)) : ONE;
    // The first month is the amount (or units) entered; growth applies from the second month.
    months.forEach((m, k) => {
      if (k > 0) factor = factor.mul(growth);
      let v: Exact;
      if (s.method === "growth") {
        const season = s.seasonality ? Exact.decimal(s.seasonality[Number(m.slice(5, 7)) - 1]!) : ONE;
        v = Exact.int(s.base).mul(factor).mul(season);
      } else if (s.method === "units") v = Exact.decimal(s.units).mul(factor).mul(Exact.int(s.price));
      else v = active(m, s.start, s.end) ? Exact.int(s.amount) : Exact.int(0);
      rev.values[k] = cents(v);
      cogs.values[k] = cents(Exact.int(rev.values[k]!).mul(pct(s.cogsPct)));
    });
  }
  const revenue = months.map((_, k) => revLines.reduce((a, x) => a + x.rev.values[k]!, 0));

  // Team: salaries by department, then payroll taxes and benefits.
  const headcount = months.map((m) => d.roles.reduce((a, r) => a + (active(m, r.start, r.end) ? r.count : 0), 0));
  const deptLines = (Object.keys(DEPARTMENTS) as PlanDepartment[])
    .filter((dep) => d.roles.some((r) => r.department === dep))
    .map((dep) => ({ dep, l: line(`pay:${dep}`, `Salaries — ${DEPARTMENTS[dep]}`, dep === "cogs" ? "cogs" : "opex") }));
  for (const { dep, l } of deptLines) {
    months.forEach((m, k) => {
      const annual = d.roles.filter((r) => r.department === dep && active(m, r.start, r.end)).reduce((a, r) => a + r.count * r.annualSalary, 0);
      l.values[k] = cents(Exact.int(annual).div(Exact.int(12)));
    });
  }
  const payrollTaxes = zeros();
  const payrollTaxEstimatedYears: number[] = [];
  if (d.roles.length) {
    const burden = line("pay:burden", "Payroll taxes and benefits", "opex");
    const override = payrollTaxOverride(d);
    const statutory = override === null ? employerPayrollTaxes(d.roles, months) : null;
    if (statutory) payrollTaxEstimatedYears.push(...estimatedWageBaseYears(d.roles, months));
    months.forEach((_, k) => {
      const salaries = deptLines.reduce((a, x) => a + x.l.values[k]!, 0);
      payrollTaxes[k] = statutory ? statutory[k]! : cents(Exact.int(salaries).mul(pct(override!)));
      burden.values[k] = payrollTaxes[k]! + d.benefitsPerHead * headcount[k]!;
    });
  }

  // Operating expenses.
  for (const e of d.expenses) {
    const l = line(`exp:${e.id}`, e.name, "opex");
    let factor = ONE;
    months.forEach((m, k) => {
      if (e.method === "fixed") {
        if (e.start && m < e.start) return;
        l.values[k] = cents(Exact.int(e.amount).mul(factor));
        factor = factor.mul(ONE.add(pct(e.growthPct)));
      } else if (e.method === "per_employee") l.values[k] = e.amount * headcount[k]!;
      else l.values[k] = cents(Exact.int(revenue[k]!).mul(pct(e.pct)));
    });
  }

  const sumSection = (section: PlanLine["section"]) => months.map((_, k) => lines.filter((l) => l.section === section).reduce((a, l) => a + l.values[k]!, 0));
  const cogs = sumSection("cogs");
  const opex = sumSection("opex");
  const grossProfit = months.map((_, k) => revenue[k]! - cogs[k]!);
  const ebitda = months.map((_, k) => grossProfit[k]! - opex[k]!);
  // Non-payroll costs are what suppliers bill (payables); payroll is paid as it's earned.
  const payrollKeys = new Set(lines.filter((l) => l.key.startsWith("pay:")).map((l) => l.key));
  const supplierCosts = months.map((_, k) => lines.filter((l) => l.section !== "revenue" && !payrollKeys.has(l.key)).reduce((a, l) => a + l.values[k]!, 0));

  // Depreciation: assets owned at the start, then each purchase straight-line from its month.
  let existingLeft = opening.fixedAssetsNet;
  const depreciation = months.map((m) => {
    const existing = Math.max(0, Math.min(d.existingDepreciation, existingLeft));
    existingLeft -= existing;
    let fresh = 0;
    for (const p of d.purchases) {
      const age = monthsFrom(p.month, m);
      if (age < 0 || age >= p.lifeMonths) continue;
      // Straight line with the rounding left for the last month, so the asset ends at exactly zero.
      const per = Math.floor(p.amount / p.lifeMonths);
      fresh += age === p.lifeMonths - 1 ? p.amount - per * (p.lifeMonths - 1) : per;
    }
    return existing + fresh;
  });
  const capex = months.map((m) => d.purchases.filter((p) => p.month === m).reduce((a, p) => a + p.amount, 0));

  // Debt: existing balance repaid at a fixed principal amount; new loans as level payments.
  let existingDebt = opening.debt;
  const loanState = d.loans.map((l) => ({ l, balance: 0, payment: loanPayment(l.principal, l.annualRatePct, l.termMonths) }));
  const interest = zeros();
  const borrowing = zeros();
  const repayments = zeros();
  const debt = zeros();
  months.forEach((m, k) => {
    const exInt = cents(Exact.int(existingDebt).mul(pct(d.existingDebtRatePct)).div(Exact.int(12)));
    const exPrin = Math.min(d.existingDebtPayment, existingDebt);
    existingDebt -= exPrin;
    let int = exInt;
    let prin = exPrin;
    for (const st of loanState) {
      if (st.l.month === m) {
        st.balance = st.l.principal;
        borrowing[k]! += st.l.principal;
        continue;
      }
      if (st.balance <= 0 || m < st.l.month) continue;
      const i = cents(Exact.int(st.balance).mul(pct(st.l.annualRatePct)).div(Exact.int(12)));
      const p = Math.min(st.payment - i, st.balance);
      st.balance -= p;
      int += i;
      prin += p;
    }
    interest[k] = int;
    repayments[k] = prin;
    debt[k] = existingDebt + loanState.reduce((a, st) => a + st.balance, 0);
  });

  const pretax = months.map((_, k) => ebitda[k]! - depreciation[k]! - interest[k]!);
  // Income tax on the calendar year's profit to date: a loss month reduces the tax on later
  // profit in the same year (never below zero for the year).
  const tax = zeros();
  {
    let year = "";
    let cumPretax = 0;
    let charged = 0;
    months.forEach((m, k) => {
      if (m.slice(0, 4) !== year) {
        year = m.slice(0, 4);
        cumPretax = 0;
        charged = 0;
      }
      cumPretax += pretax[k]!;
      const due = cumPretax > 0 ? cents(Exact.int(cumPretax).mul(pct(d.taxRatePct))) : 0;
      tax[k] = due - charged;
      charged = due;
    });
  }
  const netIncome = months.map((_, k) => pretax[k]! - tax[k]!);

  // Working capital from days outstanding over the month.
  const daysOf = (amount: Cents, days: string, m: PlanMonth) => cents(Exact.int(amount).mul(Exact.decimal(days)).div(Exact.int(daysIn(m))));
  const ar = months.map((m, k) => daysOf(revenue[k]!, d.dsoDays, m));
  const inventory = months.map((m, k) => daysOf(cogs[k]!, d.dioDays, m));
  const ap = months.map((m, k) => daysOf(supplierCosts[k]!, d.dpoDays, m));

  const prev = <T,>(arr: T[], k: number, first: T) => (k === 0 ? first : arr[k - 1]!);
  const workingCapital = months.map((_, k) => -(ar[k]! - prev(ar, k, opening.ar)) - (inventory[k]! - prev(inventory, k, opening.inventory)) + (ap[k]! - prev(ap, k, opening.ap)));
  const operating = months.map((_, k) => netIncome[k]! + depreciation[k]! + workingCapital[k]!);
  const investing = capex.map((c) => -c);
  const distributions = months.map(() => d.distributions);
  const financing = months.map((_, k) => borrowing[k]! - repayments[k]! - distributions[k]!);
  const netChange = months.map((_, k) => operating[k]! + investing[k]! + financing[k]!);
  const beginning = zeros();
  const ending = zeros();
  let cash = opening.cash;
  months.forEach((_, k) => {
    beginning[k] = cash;
    cash += netChange[k]!;
    ending[k] = cash;
  });

  let fixed = opening.fixedAssetsNet;
  const fixedAssetsNet = months.map((_, k) => (fixed += capex[k]! - depreciation[k]!));
  let eq = opening.equity;
  const equity = months.map((_, k) => (eq += netIncome[k]! - distributions[k]!));
  const otherAssets = months.map(() => opening.otherAssets);
  const otherLiabilities = months.map(() => opening.otherLiabilities);
  const totalAssets = months.map((_, k) => ending[k]! + ar[k]! + inventory[k]! + otherAssets[k]! + fixedAssetsNet[k]!);
  const totalLiabilitiesAndEquity = months.map((_, k) => ap[k]! + otherLiabilities[k]! + debt[k]! + equity[k]!);
  const openingGap = opening.cash + opening.ar + opening.inventory + opening.otherAssets + opening.fixedAssetsNet - (opening.ap + opening.otherLiabilities + opening.debt + opening.equity);
  months.forEach((m, k) => {
    // Any gap here would be a bug in the links above, not a modelling choice.
    if (totalAssets[k]! - totalLiabilitiesAndEquity[k]! !== openingGap) throw new Error(`Plan balance sheet does not balance in ${m}`);
  });

  let lowest = 0;
  ending.forEach((c, k) => {
    if (c < ending[lowest]!) lowest = k;
  });
  const cashOut = monthsUntilCashRunsOut(months.map((m, k) => ({ month: m, cashEnd: ending[k]! })));
  return {
    months,
    lines,
    pnl: { revenue, cogs, grossProfit, opex, ebitda, depreciation, interest, pretax, tax, netIncome },
    headcount,
    payrollTaxes,
    payrollTaxEstimatedYears,
    bs: { cash: ending, ar, inventory, otherAssets, fixedAssetsNet, totalAssets, ap, otherLiabilities, debt, equity, totalLiabilitiesAndEquity },
    cf: { netIncome, depreciation, workingCapital, operating, investing, borrowing, repayments, distributions, financing, netChange, beginning, ending },
    summary: {
      revenue: revenue.reduce((a, b) => a + b, 0),
      netIncome: netIncome.reduce((a, b) => a + b, 0),
      endingCash: ending[ending.length - 1]!,
      lowestCash: ending[lowest]!,
      lowestCashMonth: months[lowest]!,
      firstNegativeMonth: cashOut.kind === "runs_out" ? cashOut.firstNegativeMonth : null,
      cashOut,
      endingHeadcount: headcount[headcount.length - 1]!,
      endingDebt: debt[debt.length - 1]!,
    },
  };
}

function monthsFrom(from: PlanMonth, to: PlanMonth): number {
  const [y1, m1] = from.split("-").map(Number) as [number, number];
  const [y2, m2] = to.split("-").map(Number) as [number, number];
  return (y2 - y1) * 12 + (m2 - m1);
}
